import i18n from '@i18next/i18n';
import DELETE_ICON from '@tencent/docs-design-resources/pc/svg/toolbar_dele.svg';
import MORE_ICON from '@tencent/docs-design-resources/pc/svg/toolbar_font_more.svg';
import EDIT_ICON from '@tencent/docs-design-resources/pc/svg/toolbar_form_edit.svg';
import Button from '@tencent/dui/lib/components/Button';
import Dropdown from '@tencent/dui/lib/components/Dropdown';
import Menu from '@tencent/dui/lib/components/Menu';
import Item from '@tencent/dui/lib/components/Menu/Item';
import Modal from '@tencent/dui/lib/components/Modal';
import { getCookieUid } from '@tencent/tencent-doc-report';
import classNames from 'classnames';
import React, { ReactElement, useState } from 'react';
import { ICommentWithVipInfo, IDeleteCommentApi } from '../../../../type';
import { formatTime } from '../../../../utils';
import AvatarNickNameplate from '../../avatar-nick-nameplate';
import CommentInput from '../../comment-input/comment-input';
import { EventCodeType, TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import Style from '../../index.less';

interface ICommentGroupItemProps extends IDeleteCommentApi {
    commentInfo: ICommentWithVipInfo;
    commentIndex: number;
    groupId: string;
    isLastItem: boolean;
    isCommentOwner?: boolean;   // 是否是当前批注的作者
    isGroupActive?: boolean;    // 当前批注组有没有聚焦
    onEditStateChanged?: (commentId?: string, isEditing?: boolean) => void;
}

const CommentGroupItem: React.FC<ICommentGroupItemProps> = (props: ICommentGroupItemProps) => {
    const { commentInfo, commentIndex, groupId } = props;
    // 使用状态控制而不是css是因为，焦点会被dui dropdown抢走，导致鼠标移入下拉菜单时，hover态消失
    const [isHover, setHover] = useState(false);

    const handleMouseEnter = (e) => {
        if (!props.isGroupActive) return;
        setHover(true);
    };

    const handleMouseLeave = (e) => {
        if (!props.isGroupActive) return;
        // 避免鼠标hover到会员铭牌上触发的handleMouseLeave事件
        if (e.currentTarget?.contains(e.relatedTarget)) {
            return;
        }
        setHover(false);
    };

    /**
    * 避免当鼠标在该元素区域内时，需要将鼠标移入/移出才能触发hover
    */
    const handleMouseMove = (e) => {
        if (!props.isGroupActive || isHover) return;
        setHover(true);
    };

    const handleEdit = () => {
        props.onEditStateChanged?.(commentInfo.id, true);
    };

    const handleDeleteComment = () => {
        Modal.confirm({
            title: i18n.t('删除批注'),
            content: i18n.t('单条批注删除后不可恢复，确认删除吗？'),
            okButtonType: 'warning',
            okText: i18n.t('删除'),
            onOk: () => {
                props.deleteComment(groupId, commentInfo.id);
            },
        });
    };

    const handleDeleteAllComments = () => {
        Modal.confirm({
            title: i18n.t('删除批注'),
            content: i18n.t('删除后，该批注下方的回复也将消失。确认删除吗？'),
            okButtonType: 'warning',
            okText: i18n.t('删除'),
            onOk: () => {
                props.deleteCommentGroup(groupId);
                report(TDWActionType.DELETE_COMMENTS, EventCodeType.DOC_PPT_COMMENTS_SLIDEBAR_DELETE_COMMENTS);
            },
        });
    };

    const report = (action: string, eventcode: string) => {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.COMMENTS_SLIDEBAR,
            action,
            eventcode,
        });
    };

    const renderMoreBtn = () => {
        const { isOwner } = window.clientVars;
        const uid = window.clientVars.userInfo.uid ?? getCookieUid();
        const isCommentOwner = uid === commentInfo.uin;
        let dropContent: ReactElement | undefined;
        if (isCommentOwner) {
            dropContent = <Menu>
                <Item icon={EDIT_ICON} className={Style['more-menu-item']} onClick={handleEdit}>{i18n.t('编辑')}</Item>
                {
                    commentIndex === 0
                        ? <Item icon={DELETE_ICON} className={Style['more-menu-item']} onClick={handleDeleteAllComments}>{i18n.t('删除该组批注')}</Item>
                        : <Item icon={DELETE_ICON} className={Style['more-menu-item']} onClick={handleDeleteComment}>{i18n.t('删除')}</Item>
                }
            </Menu>;
        } else if (isOwner && commentIndex === 0) {
            // 文档作者可以删除整组批注
            dropContent = <Menu>
                <Item icon={DELETE_ICON} className={Style['more-menu-item']} onClick={handleDeleteAllComments}>{i18n.t('删除该组批注')}</Item>
            </Menu>;
        }
        if (!dropContent) return <></>;
        return (<Dropdown dropContent={dropContent} className={Style['more-menu-btn-wrap']} style={{ visibility: isHover ? 'visible' : 'hidden' }}>
            <Button type='plain' className={Style['more-menu-btn']}><img src={MORE_ICON} alt="" style={{ pointerEvents: 'none' }}/></Button>
        </Dropdown>);
    };

    const renderTimes = () => <span className={Style['comment-user-info-times']} style={{ visibility: !isHover ? 'visible' : 'hidden' }}>
        {formatTime(commentInfo.times)}
    </span>;

    return <div
        className={classNames({
            [Style['comment-group-item']]: true,
            [Style['comment-group-item-hover']]: isHover,
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        style={props.isLastItem ? {} : { borderBottom: '1px solid #f4f4f4' }}
    >
        <AvatarNickNameplate commentInfo={commentInfo}>
            {renderMoreBtn()}
            {renderTimes()}
        </AvatarNickNameplate>
        <CommentInput
            groupId={groupId}
            commentId={commentInfo.id}
            content={commentInfo.content}
            readOnly={true}
        />
    </div>;
};

export default CommentGroupItem;
